import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/legacy/utils/error-handling/throwifApiError'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { requestAuditLogQueryKey } from './detail/components/audit-log/useGetAuditLogsByRequestId'
import { requestCommentsQueryKey } from './detail/components/comments/api/useGetRequestComment'
import { RequestStepsQueryKey } from './useGetRequestSteps'
import { RequestDetailQueryKey } from './useRequestDetail'

type SetBlockSkipParams = {
  requestId: string
  blockId: string
  skip: boolean
  comment?: string
}

const setBlockSkip = async (token: string, { requestId, blockId, skip, comment }: SetBlockSkipParams) => {
  const apiClient = new RequestsHttpClient({ authorization: token })

  const { data, error } = await apiClient.POST('/{id}/blocks/{blockId}/skip', {
    params: {
      path: { id: requestId, blockId },
    },
    body: {
      skip,
      comment,
    },
  })

  throwIfApiError(error)
  return data
}

export const useSetBlockSkip = () => {
  const { t } = useTranslation('requests')
  const { enqueueSnackbar } = useSnackbar()
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: SetBlockSkipParams) => {
      return setBlockSkip(await getToken(), params)
    },
    onSuccess: (_data, variables) => {
      enqueueSnackbar(
        variables.skip ? t('timeline-overview.step-skipped-message') : t('timeline-overview.step-un-skipped-message'),
        {
          variant: 'success',
        }
      )
      void queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, variables.requestId] })
      void queryClient.invalidateQueries({ queryKey: [RequestStepsQueryKey, variables.requestId] })
      void queryClient.invalidateQueries({ queryKey: [requestCommentsQueryKey, variables.requestId] })
      void queryClient.invalidateQueries({ queryKey: [requestAuditLogQueryKey, variables.requestId] })
    },
    onError: (error, variables) => {
      console.error('Error setting block skip', error)
      enqueueSnackbar(variables.skip ? t('timeline-overview.step-skip-error') : t('timeline-overview.step-unskip-error'), {
        variant: 'error',
      })
    },
  })
}
